import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  QrCode, Wallet, ShieldCheck, Building2, Star, ArrowRight, Smartphone,
  Fuel, SquareParking, UtensilsCrossed, Bike, SprayCan, Scissors, Users,
  Landmark, Gift, ScanLine, MousePointerClick, CreditCard, CheckCircle2,
  Zap, Eye, Award, Shuffle, User, TrendingUp,
} from "lucide-react";
import { TiplymeLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";

const WORKPLACE_BG = "https://images.unsplash.com/photo-1669102976935-c1ceb75267ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxnYXMlMjBzdGF0aW9uJTIwc291dGglMjBhZnJpY2F8ZW58MHx8fHwxNzkwMjM4OTA2fDA&ixlib=rb-4.1.0&q=85";
const WORKER = "https://images.unsplash.com/photo-1787672357797-f5fa35bb0d18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGFmcmljYW4lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwwfHx8fDE3OTAyMzg5MDZ8MA&ixlib=rb-4.1.0&q=85";

const steps = [
  { n: "01", icon: ScanLine, t: "Scan or Tap", d: "Scan the worker's QR code or tap their TiplyMe NFC card." },
  { n: "02", icon: MousePointerClick, t: "Choose", d: "Select a suggested tip amount or enter your own." },
  { n: "03", icon: CreditCard, t: "Pay", d: "Complete the payment securely from your phone." },
  { n: "04", icon: CheckCircle2, t: "Done", d: "The worker receives the payment in their TiplyMe wallet." },
];

const earnings = ["Today's earnings", "Weekly earnings", "Monthly earnings", "Individual transactions", "Wallet balance", "Cash-outs", "Customer ratings", "Earnings history"];

const cashouts = [
  { icon: Landmark, t: "Bank", d: "Transfer earnings to a bank account." },
  { icon: Wallet, t: "E-Wallet", d: "Send earnings to a supported personal wallet." },
  { icon: Smartphone, t: "Airtime", d: "Convert earnings into airtime." },
  { icon: Gift, t: "Vouchers", d: "Buy a voucher for yourself — or gift it to someone else." },
];

const workers = [
  { icon: Fuel, t: "Petrol Attendants", d: "A customer fills up. They scan. They tip." },
  { icon: SquareParking, t: "Parking Attendants", d: "No loose change? No problem. Tap and pay." },
  { icon: UtensilsCrossed, t: "Waiters & Service Staff", d: "Reward great service directly." },
  { icon: Bike, t: "Delivery Workers", d: "Recognise the person who brought it to your door." },
  { icon: SprayCan, t: "Cleaners", d: "Make digital tipping simple." },
  { icon: Scissors, t: "Beauty & Personal Services", d: "Reward the people who make you feel great." },
  { icon: Users, t: "And Many More", d: "If you provide a service, TiplyMe helps customers reward you." },
];

const whys = [
  { icon: Smartphone, t: "Cashless", d: "Customers don't need physical cash to reward great service." },
  { icon: Zap, t: "Simple", d: "No complicated process. Scan or tap and pay." },
  { icon: User, t: "Personal", d: "Every worker gets their own digital identity." },
  { icon: Eye, t: "Transparent", d: "Payments, earnings and cash-outs are recorded." },
  { icon: Award, t: "Reputation", d: "Customer ratings help workers build a digital service profile." },
  { icon: Shuffle, t: "Flexible", d: "Access earnings through multiple cash-out options." },
];

const experiences = [
  { tag: "Customer", flow: "Scan → Choose → Pay", d: "A frictionless payment experience designed to take seconds.", icon: ScanLine },
  { tag: "Worker", flow: "Earn → Track → Cash Out", d: "A personal wallet and earnings dashboard.", icon: Wallet },
  { tag: "Business", flow: "Onboard → Manage → Reconcile", d: "A central platform for managing workers, payments and operations.", icon: Building2 },
];

const businessTools = ["Worker onboarding", "QR code generation", "NFC assignment", "Payment tracking", "Earnings management", "Cash-out processing", "Ratings", "Financial reconciliation", "Reporting and exports", "Operational management"];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay }} className={className}>
    {children}
  </motion.div>
);

const Eyebrow = ({ children, light }) => (
  <span className={`overline-label ${light ? "text-brand-orange" : "text-brand-green"}`}>{children}</span>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border/60 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <TiplymeLogo />
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="rounded-full font-semibold">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="nav-register-link">
              <Button className="rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">Get TiplyMe</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 md:grid-cols-2 md:py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Eyebrow>TIPLYME</Eyebrow>
            <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.12] tracking-tight sm:text-5xl lg:text-6xl">
              Get tipped.<br />
              Get paid.<br />
              <span className="rounded-lg bg-brand-orange px-2 text-brand-blueDark">Get recognised.</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
              The simple way for gig workers to receive digital tips and payments. With a QR code or an NFC tap, customers pay directly from their phone — using their preferred payment method.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-2">
              {["Scan.", "Pay.", "Tip."].map((w) => (
                <span key={w} className="rounded-full border border-brand-green/25 bg-brand-green/5 px-4 py-1.5 font-heading text-sm font-bold text-brand-green">{w}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-worker">
                <Button size="lg" className="rounded-full bg-brand-green px-7 font-semibold text-white hover:bg-brand-green/90">
                  Become a TiplyMe worker <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-signin">
                <Button size="lg" variant="outline" className="rounded-full border-brand-green/30 px-7 font-semibold text-brand-green hover:bg-brand-green/5">
                  Sign in
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-brand-green" /> Secure payments</span>
              <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-brand-orange" /> Ratings build trust</span>
              <span className="flex items-center gap-1.5"><Wallet className="h-4 w-4 text-brand-green" /> Instant wallet</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 }} className="relative">
            <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-brand-green/10 blur-2xl" />
            <div className="grain relative overflow-hidden rounded-[2rem] border border-border/60 shadow-2xl">
              <img src={WORKPLACE_BG} alt="Service worker at a South African forecourt" className="h-56 w-full object-cover" />
              <div className="relative -mt-14 px-6 pb-6">
                <div className="rounded-2xl border border-border/60 bg-white p-5 shadow-xl">
                  <div className="flex items-center gap-4">
                    <img src={WORKER} alt="Thabo" className="h-16 w-16 rounded-2xl object-cover ring-2 ring-brand-green/20" />
                    <div>
                      <p className="font-heading text-lg font-bold">Thabo Mokoena</p>
                      <p className="text-sm text-muted-foreground">Petrol Attendant · Sasol N1 North</p>
                      <p className="mt-1 flex items-center gap-1 text-sm font-semibold text-brand-orange"><Star className="h-4 w-4 fill-brand-orange" /> 4.8 <span className="font-normal text-muted-foreground">(27)</span></p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-2">
                    {[2, 4, 5, 10].map((a) => (
                      <div key={a} className="rounded-xl border border-brand-green/20 bg-brand-green/5 py-3 text-center font-heading font-bold text-brand-green">R{a}</div>
                    ))}
                  </div>
                  <Button className="mt-4 w-full rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90">Tip R5 →</Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro band */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center">
          <Reveal>
            <h2 className="font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">Your service deserves a tip.</h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
              Every day, millions of people provide great service — petrol attendants, car guards, waiters, delivery riders, cleaners, parking attendants and beauty professionals. But when it comes to tipping, cash isn't always available. TiplyMe changes that.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2 text-sm font-semibold text-brand-green">
              <span className="rounded-full bg-card px-4 py-1.5 shadow-sm">No cash</span>
              <span className="rounded-full bg-card px-4 py-1.5 shadow-sm">No awkward change</span>
              <span className="rounded-full bg-card px-4 py-1.5 shadow-sm">No complicated process</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <Reveal>
          <Eyebrow>HOW IT WORKS</Eyebrow>
          <h2 className="mt-3 max-w-xl font-heading text-3xl font-extrabold tracking-tight">Each worker gets a unique QR code and NFC identity.</h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08}>
              <div className="group h-full rounded-2xl border border-border/50 bg-card p-6 shadow-sm transition-transform hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-green/10 text-brand-green"><s.icon className="h-6 w-6" /></div>
                  <span className="font-heading text-3xl font-extrabold text-brand-green/15">{s.n}</span>
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold">{s.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* More than a tip — earnings */}
      <section className="bg-brand-blueDark text-white">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-20 lg:grid-cols-2">
          <Reveal>
            <span className="overline-label text-brand-orange">MORE THAN A TIP</span>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Your earnings, your reputation, your digital identity.</h2>
            <p className="mt-5 max-w-md leading-relaxed text-white/70">
              TiplyMe is a personal earnings platform. Every payment is recorded, giving workers a clear picture of what they're earning over time.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-3">
              {earnings.map((e) => (
                <div key={e} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-medium">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-orange" /> {e}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Profile / reputation */}
      <section className="mx-auto max-w-5xl px-5 py-20 text-center">
        <Reveal>
          <Eyebrow>YOUR TIPLYME PROFILE</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight">Turn every interaction into your reputation.</h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-muted-foreground">
            Your profile can show your name, photograph and customer rating when someone is about to pay you. Ratings help gig workers build a digital reputation that can support them when looking for new work.
          </p>
          <p className="mt-6 font-heading text-lg font-bold text-brand-green">Your service creates your reputation. Your reputation creates new opportunities.</p>
        </Reveal>
      </section>

      {/* Get paid your way — cashouts */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <Reveal>
            <Eyebrow>GET PAID YOUR WAY</Eyebrow>
            <h2 className="mt-3 max-w-xl font-heading text-3xl font-extrabold tracking-tight">Your money. Your choice.</h2>
            <p className="mt-3 max-w-xl text-muted-foreground">When you're ready to cash out, TiplyMe gives you different ways to use your earnings.</p>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {cashouts.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-border/50 bg-card p-6 shadow-sm transition-transform hover:-translate-y-1">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-orange/15 text-brand-orange"><c.icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 font-heading text-lg font-bold">{c.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Made for the people */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <Reveal>
          <Eyebrow>MADE FOR THE PEOPLE WHO KEEP THE WORLD MOVING</Eyebrow>
          <h2 className="mt-3 max-w-xl font-heading text-3xl font-extrabold tracking-tight">Designed for people who earn through service.</h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {workers.map((w, i) => (
            <Reveal key={w.t} delay={(i % 3) * 0.06}>
              <div className="flex h-full items-start gap-4 rounded-2xl border border-border/50 bg-card p-6 shadow-sm transition-transform hover:-translate-y-1">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-green/10 text-brand-green"><w.icon className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-heading font-bold">{w.t}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{w.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* For customers / workers */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-20 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-3xl border border-border/50 bg-card p-8 shadow-sm">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-green/10 text-brand-green"><ScanLine className="h-6 w-6" /></div>
              <span className="mt-4 block overline-label text-brand-green">FOR CUSTOMERS</span>
              <h3 className="mt-2 font-heading text-2xl font-extrabold tracking-tight">See. Scan. Support.</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">You don't need an app. Scan the worker's QR code or tap their NFC tag, see their profile and rating, then choose how much to give.</p>
              <p className="mt-4 font-heading font-bold text-brand-green">Scan → Choose → Pay</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-3xl border border-border/50 bg-card p-8 shadow-sm">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-orange/15 text-brand-orange"><Wallet className="h-6 w-6" /></div>
              <span className="mt-4 block overline-label text-brand-orange">FOR GIG WORKERS</span>
              <h3 className="mt-2 font-heading text-2xl font-extrabold tracking-tight">Your work. Your earnings. Your TiplyMe.</h3>
              <p className="mt-3 leading-relaxed text-muted-foreground">Create your profile, complete verification, and receive your unique QR/NFC identity. Put it on your lanyard, card, uniform, work station or vehicle.</p>
              <Link to="/register"><Button className="mt-5 rounded-full bg-brand-green font-semibold text-white hover:bg-brand-green/90" data-testid="workers-cta">Get started <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* For businesses */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>FOR BUSINESSES & ORGANISATIONS</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight">A digital tipping infrastructure for your service workforce.</h2>
            <p className="mt-4 max-w-md leading-relaxed text-muted-foreground">Onboard, manage and monitor large numbers of gig workers. A central administration platform gives authorised teams visibility across users, payments and operations.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-3">
              {businessTools.map((b) => (
                <div key={b} className="flex items-center gap-2 rounded-xl border border-border/50 bg-card px-4 py-3 text-sm font-medium shadow-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-green" /> {b}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Three experiences */}
      <section className="border-y border-border/60 bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <Reveal>
            <Eyebrow>ONE PLATFORM. THREE EXPERIENCES.</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight">Connecting service, recognition and payments.</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {experiences.map((e, i) => (
              <Reveal key={e.tag} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-border/50 bg-card p-7 shadow-sm">
                  <div className="grid h-12 w-12 place-items-center rounded-xl bg-brand-green/10 text-brand-green"><e.icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 font-heading text-xl font-extrabold">{e.tag}</h3>
                  <p className="mt-1 font-heading text-sm font-bold text-brand-orange">{e.flow}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{e.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Tiplyme */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <Reveal>
          <Eyebrow>WHY TIPLYME?</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight">Built for a cashless world.</h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {whys.map((w, i) => (
            <Reveal key={w.t} delay={(i % 3) * 0.06}>
              <div className="h-full rounded-2xl border border-border/50 bg-card p-6 shadow-sm transition-transform hover:-translate-y-1">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-green/10 text-brand-green"><w.icon className="h-5 w-5" /></div>
                <h3 className="mt-4 font-heading text-lg font-bold">{w.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-brand-green text-white">
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-5 py-20 text-center">
          <Reveal>
            <span className="overline-label text-brand-orange">THE FUTURE OF TIPPING IS DIGITAL</span>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-5xl">Scan. Pay. Tip.</h2>
            <p className="mx-auto mt-4 max-w-xl leading-relaxed text-white/80">Because a tip is more than money — it's a thank you. And everyone who provides great service deserves an easy way to receive one.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/register" data-testid="cta-get-tiplyme">
                <Button size="lg" className="rounded-full bg-brand-orange px-7 font-bold text-brand-blueDark hover:bg-brand-orangeLight">Get TiplyMe</Button>
              </Link>
              <Link to="/register" data-testid="cta-become-worker">
                <Button size="lg" variant="outline" className="rounded-full border-white/40 bg-white/5 px-7 font-semibold text-white hover:bg-white/15">Become a TiplyMe worker</Button>
              </Link>
              <a href="mailto:partners@tiplyme.co.za" data-testid="cta-partner">
                <Button size="lg" variant="outline" className="rounded-full border-white/40 bg-white/5 px-7 font-semibold text-white hover:bg-white/15">Partner with TiplyMe</Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-border/60 bg-card">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-8 text-sm text-muted-foreground md:flex-row">
          <TiplymeLogo />
          <p>Scan. Pay. Tip. · tiplyme.com · tiplyme.co.za — making it easier to reward the people who serve us every day.</p>
        </div>
      </footer>
    </div>
  );
}
